<template>
    <label class="delayed-chat-input">
        <input
            v-model.trim="message"
            type="text"
            class="delayed-chat-input__input"
            :placeholder="assistent?.widget.greeting ?? placeholderText"
            @keydown.enter="onInput"
        />
        <span :class="submitComponentClass">
            <icon-send @click="onInput" />
        </span>
    </label>
</template>

<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import useConfiguration from '@/composables/useConfiguration';
    import IconSend from '@/assets/icons/send.svg?component';
    import useWidgetState, { State } from '@/composables/useWidgetState';
    import useWidgetAppearance from '@/composables/useWidgetAppearance';
    import Chat from '@/chat/Chat';

    const chat = Chat.getInstance();
    const { assistent } = useConfiguration();
    const { state } = useWidgetState();
    const { placeholderText } = useWidgetAppearance();
    const message = ref('');

    const canSendMessage = computed(() => {
        return message.value.trim().length > 0 && !chat.loading.value;
    });

    export interface Props {
        withMargin: boolean,
    }

    const props = withDefaults(defineProps<Props>(), {
        withMargin: false,
    });

    const submitComponentClass = computed(() => {
        return {
            'delayed-chat-input__submit': true,
            'delayed-chat-input__submit--with-margin': props.withMargin,
            'delayed-chat-input__submit--disabled': !canSendMessage.value,
        };
    });

    async function onInput() {
        if (!assistent.value) {
            return;
        }

        if (!canSendMessage.value) {
            return;
        }

        const localMessage = message.value;
        message.value = '';

        await chat.sendMessage(localMessage);
        state.value = State.Opened;
    }

    // prevent message from mutating when canSendMessage is false
    watch(() => message.value, () => {
        if (!canSendMessage.value) {
            message.value = '';
        }
    });
</script>

<style scoped lang="scss">
    .delayed-chat-input {
        display: grid;
        grid-template-columns: auto min-content;
        align-items: center;
        gap: 1rem;
        border: 1px solid lightgrey;
        border-radius: 1rem;
        overflow: hidden;
        background-color: white;

        &--with-margin {
            margin-left: 0.8rem;
            margin-right: 2.5rem;
        }

        &__input {
            appearance: none;
            border: 0;
            outline: 0;
            padding: 1rem;
            font-size: 16px;
            text-overflow: ellipsis;
        }

        &__submit {
            color: var(--color-balloon);
            padding: 0.8rem;
            display: grid;
            border-radius: 10px;
            cursor: pointer;

            &--disabled {
                color: lightgrey;
                cursor: not-allowed;
            }

            svg {
                width: 24px;
                height: 24px;
            }
        }
    }
</style>
